import React from "react";
import { MdClose } from "react-icons/md";
import { FaRegStar, FaStar } from "react-icons/fa6";
import { MealDetails } from "../../../redux/mealManagement/mealManagementSlice";

interface MealDetailsModalProps {
  meal: MealDetails;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
}

const MealDetailsModal: React.FC<MealDetailsModalProps> = ({
  meal,
  isOpen,
  onClose,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderRating = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FaRegStar key={i} className="text-yellow-400" />);
      } else {
        stars.push(<FaRegStar key={i} className="text-gray-300" />);
      }
    }

    return (
      <div className="flex items-center gap-1">
        <div className="flex">{stars}</div>
        <span className="text-sm text-gray-600 ml-1">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const getStatusColor = (status: string, isAvailable: boolean) => {
    if (status === "active") {
      if (!isAvailable) {
        return "bg-yellow-100 text-yellow-600";
      }
      return "bg-green-100 text-green-600";
    }
    return "bg-red-100 text-red-600";
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
            <span className="ml-2">Loading meal details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b">
          <div className="flex items-start gap-4">
            {meal.image && (
              <img
                src={meal.image}
                alt={meal.name}
                className="w-24 h-24 object-cover rounded-lg"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/assets/placeholder-meal.svg";
                }}
              />
            )}
            <div>
              <h2 className="text-2xl font-semibold text-secondary mb-2">
                {meal.name}
              </h2>
              <p className="text-sm text-gray-600 mb-1">
                Meal Item details and management
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>Category: {meal.category}</span>
                <span>•</span>
                <span>Updated: {formatDate(meal.updatedAt)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <MdClose className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price:
              </label>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold">
                  {formatCurrency(meal.price)}
                </p>
                {meal.isDiscount && (
                  <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                    {meal.discountAmount}% OFF
                  </span>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status:
              </label>
              <span
                className={`${getStatusColor(meal.status, meal.isAvailable)} 
                  inline-block px-3 py-1 rounded-full text-sm font-medium capitalize`}
              >
                {meal.isAvailable ? "Available" : "Unavailable"}
              </span>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating:
              </label>
              {renderRating(meal.rating || 0)}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Orders:
              </label>
              <p className="text-lg font-semibold">
                {meal.orderStats?.length || 0}
              </p>
            </div>
          </div>

          {/* Description */}
          {meal.description && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description:
              </label>
              <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">
                {meal.description}
              </p>
            </div>
          )}

          {/* Detailed Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">
                General Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Created Date:</span>
                  <span className="text-sm">{formatDate(meal.createdAt)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Last Updated:</span>
                  <span className="text-sm">{formatDate(meal.updatedAt)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Availability:</span>
                  <span
                    className={`text-sm font-medium ${
                      meal.isAvailable ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {meal.isAvailable ? "Available" : "Not Available"}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Discount:</span>
                  <span className="text-sm">
                    {meal.isDiscount
                      ? `${meal.discountAmount}% OFF`
                      : "No Discount"}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">
                Performance Metrics
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Order Stats:</span>
                  <span className="text-sm">
                    {meal.orderStats?.length || 0} recorded
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Rating Stats:</span>
                  <span className="text-sm">
                    {meal.ratingStats?.length || 0} recorded
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium">
                    Actions Available:
                  </span>
                  <span className="text-sm">{meal.actions || "None"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Action Buttons */}
        {/* <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Meal ID: {meal._id}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close
            </button>
            <button
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors flex items-center gap-2"
              onClick={() => {
                // TODO: Implement hide/unhide functionality
                console.log('Hide/Show meal:', meal._id);
              }}
            >
              <span>🙈</span>
              Hide Item
            </button>
            <button
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
              onClick={() => {
                // TODO: Implement delete functionality
                if (window.confirm('Are you sure you want to delete this meal?')) {
                  console.log('Delete meal:', meal._id);
                }
              }}
            >
              <span>🗑️</span>
              Delete Item
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default MealDetailsModal;
